import {
  AmountCell,
  DateCell,
  Typography,
} from '@omnipresentgroup/design-system';
import type { ContractorInvoice } from '@omnipresentgroup/types';
import {
  type CellContext,
  type ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import {
  Actions,
  ContractorInvoiceActions,
} from '../../contractor-invoice-actions';
import { ContractorInvoiceStatus } from '../../contractor-invoice-status';

export enum ColumnHeaders {
  invoice = 'Invoice',
  client = 'Client',
  contractor = 'Contractor',
  dueDate = 'Due',
  amount = 'Amount',
  state = 'Status',
  raisedAt = 'Raised',
  paidAt = 'Paid on',
  cancel = 'Cancel',
}

export const useContractorInvoicesTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<ContractorInvoice, unknown>[];
}) => {
  const defaultRenderer = (info: CellContext<ContractorInvoice, string>) => (
    <Typography as="span" size="14">
      {info?.getValue() ?? ''}
    </Typography>
  );

  const columnHelper = createColumnHelper<ContractorInvoice>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<ContractorInvoice, any>[] = useMemo(
    () => [
      columnHelper.accessor('invoiceRef', {
        cell: defaultRenderer,
        header: () => ColumnHeaders.invoice,
      }),
      columnHelper.accessor('client', {
        cell: (info) => info.row.original.client.name,
        header: () => ColumnHeaders.client,
      }),
      columnHelper.accessor('contractor', {
        cell: (info) => info.row.original.contractor.name,
        header: () => ColumnHeaders.contractor,
      }),
      columnHelper.accessor('dueDate', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.dueDate,
      }),
      columnHelper.accessor('raisedAt', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.raisedAt,
      }),
      columnHelper.accessor('paidAt', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.paidAt,
      }),
      columnHelper.accessor('amount', {
        cell: (info) => (
          <AmountCell
            amount={info.row.original.amount}
            currency={info.row.original.currency}
          />
        ),
        header: () => ColumnHeaders.amount,
      }),
      columnHelper.accessor('state', {
        cell: (info) => (
          <ContractorInvoiceStatus invoice={info?.row.original} />
        ),
        header: () => ColumnHeaders.state,
      }),
      columnHelper.display({
        id: 'cancel',
        cell: (info) => (
          <ContractorInvoiceActions
            invoice={info?.row.original}
            actions={[Actions.CANCEL]}
          />
        ),
        size: 45,
        meta: {
          onClickStopPropagation: true,
        },
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride],
  );

  return {
    columns,
    columnHelper,
  };
};
